import { TripDisplayStatus } from '../../models/Trips';

// Mass bonusing — the rules shared by the Trips toolbar, the grid, the mobile
// list and the amount dialog. Kept here (and free of React) so the eligibility
// rule and the amount bounds have one definition and can be tested directly.

// A mass bonus is a two-digit whole-dollar amount: $0–$99, no cents. The dispatch
// API itself allows more (0–500, PayoutController.MAX_BONUS) — this is the
// tighter product rule, and it's the one the field enforces, because an amount
// here is multiplied by every trip in the selection.
export const MAX_MASS_BONUS = 99;

// Only trips sitting in the "Needs Attention - Manual Bonus" section can be mass
// bonused — that section is the whole point of the feature, and a bulk write
// against, say, a completed trip is not something to make one click away.
export const isMassBonusEligibleTrip = (trip) =>
  !!trip && trip.displayStatus === TripDisplayStatus.needs_attention;

// What the full-ADMIN override still won't offer: a cancelled trip, or one a
// driver already holds. A bonus is there to get a trip claimed, so neither has a
// use for one.
export const isMassBonusOverrideTrip = (trip) =>
  !!trip && trip.displayStatus !== TripDisplayStatus.cancelled && !trip.driver;

// The trips the picker offers. `anyStatus` is the full-ADMIN override: every
// on-screen trip that is neither cancelled nor claimed, whatever section it sits
// in. It's opt-in per selection (see TripsDaily), so the Needs Attention rule
// above stays the default even for ADMINs. The bonus endpoint itself has no
// status gate, so this is the only place the rule lives.
export const getMassBonusTrips = (trips = [], { anyStatus = false } = {}) =>
  trips.filter(anyStatus ? isMassBonusOverrideTrip : isMassBonusEligibleTrip);

// "4:30 PM CDT · Austin ➔ Houston" — how a trip is named in the mass-bonus flow.
// `f` is useFormatters().
export const formatBonusTripLabel = (f, trip) => {
  const departure = f.date.toTimezoneLocaleString(
    trip.departureTime,
    trip.originCity.timeZone,
    f.date.DateFormats.TIME_SIMPLE_WITH_TIMEZONE,
  );
  return `${departure} · ${trip.originCity.cityName} ➔ ${trip.destinationCity.cityName}`;
};

export const parseBonusAmount = (value) => {
  const parsed = Number.parseFloat(String(value ?? '').replace(/[$,\s]/g, ''));
  return Number.isFinite(parsed) ? parsed : NaN;
};

// $0 is deliberately allowed: it's how ops takes a mass bonus back off.
export const validateBonusAmount = (value) => {
  const amount = parseBonusAmount(value);
  if (Number.isNaN(amount)) return 'Enter a bonus amount';
  if (amount < 0) return 'Bonus cannot be negative';
  if (!Number.isInteger(amount)) return 'Bonus must be a whole dollar amount';
  if (amount > MAX_MASS_BONUS) return `Bonus cannot be more than $${MAX_MASS_BONUS}`;
  return null;
};

// What the amount field will hold at all: nothing, or one or two digits. Enforced
// as you type rather than reported afterwards, so a third digit or a decimal point
// can't be entered in the first place.
export const isAllowedBonusInput = (value) => /^\d{0,2}$/.test(String(value ?? ''));

// The endpoint SETS manualBonus, it does not add to it (Trip.setBonus assigns
// payoutItemized.manualBonus), so any trip already carrying one has it replaced.
// Counted up front so the dialog can say so before ops commits.
export const countExistingManualBonuses = (trips = []) =>
  trips.filter((t) => (t?.payoutItemized?.manualBonus || 0) > 0).length;

export const summarizeBulkResults = (results = []) => {
  const applied = results.filter((r) => r.success);
  const failed = results.filter((r) => !r.success);
  return {
    applied,
    failed,
    appliedCount: applied.length,
    failedCount: failed.length,
    // How many trips this run actually attempted. The caller narrows its live
    // selection down to the failures so they can be retried, so the selection is
    // no longer the denominator once a result exists — reading it as one prints
    // "applied to 2 of 1 trips".
    attemptedCount: results.length,
    allSucceeded: failed.length === 0,
  };
};

// Which of the attempted trips still don't carry the amount, judged against the
// RELOADED day rather than the API's answers.
//
// A 200 from the bonus endpoint is not proof of a write: Trip.setBonus catches its
// own save error and the controller still returns 200. So a trip that "succeeded"
// but reloaded with its old bonus did not get bonused, and belongs with the
// outright failures. A trip that has left the day entirely can't be judged here
// and is left alone rather than reported as a failure.
export const findUnresolvedTrips = (summary, amount, reloadedTrips) => {
  // An empty array is a real answer (the day reloaded and holds nothing we
  // bonused). A non-array means no reload happened at all, so nothing was
  // verified -- report every 200 as unproven rather than as persisted. Callers
  // should handle the failed reload themselves; this only stops a missing
  // reload from reading as success.
  if (!Array.isArray(reloadedTrips)) {
    return [
      ...(summary?.failed || []).map((r) => r.tripId),
      ...(summary?.applied || []).map((r) => r.tripId),
    ];
  }

  const reloaded = new Map(reloadedTrips.map((t) => [t.id, t]));
  const didNotStick = (summary?.applied || [])
    .map((r) => r.tripId)
    .filter((id) => {
      const trip = reloaded.get(id);
      return trip ? (trip.payoutItemized?.manualBonus || 0) !== amount : false;
    });
  return [...(summary?.failed || []).map((r) => r.tripId), ...didNotStick];
};
